import {Controller} from "@hotwired/stimulus"

/**
 * CollapseController allows to collapse/expand content. The triggering element
 * must provide ID selector for the content. Hidden class is toggled on
 * the content. Expand and collapse classes are swapped on the toggling element.
 * The controller also allows to search by name and highlight matches.
 *
 * If a local storage key is provided, the state is initialized from LS &
 * state changes are store in LS.
 *
 * %div{data: { controller: 'collapse',
 *              collapse_hidden_class: 'hidden',
 *              collapse_expand_class: 'icon--expand',
 *              collapse_collapse_class: 'icon--collapse',
 *              collapse_search_match_class: 'matching-search',
 *              collapse_local_storage_key_value: 'fancy-key',
 *              collapse_scroll_local_storage_key_value: 'scroll-key',
 *              }}
 *   %input{data: { action: 'change->collapse#search' }}
 *   %button{data: { action: 'click->collapse#collapseAll' }} collapse all
 *   %button{data: { action: 'click->collapse#expandAll' }} expand all
 *
 *   %div{data: { collapse_target: 'scrollable' }}
 *      %div
 *        %button{type: :button,
 *                data: { action: 'click->collapse#toggle',
 *                        collapse_id_param: id,
 *                        collapse_icon_id_param: icon_id }} toggler
 *        %div{id: id, data: { collapse_target: 'searchable' }} content to be collapsed
 *
 *      %div
 *        %button{type: :button,
 *                data: { action: 'click->collapse#toggle',
 *                        collapse_id_param: id }} toggler
 *        %div{id: id data: { collapse_target: 'searchable' }} content to be collapsed
 */
export default class extends Controller {
  static classes = ['collapse', 'expand', 'hidden', 'searchMatch']
  static values = {
    localStorageKey: String,
    scrollLocalStorageKey: String
  }
  static targets = ['currentAncestor', 'current', 'scrollable', 'searchable', 'searchCount', 'searchResults']

  connect() {
    super.connect();
    this._initFromLocalStorage();
    if (this.hasScrollableTarget) {
      this.scrollTracker = this._trackScroll.bind(this);
      this.scrollableTarget.addEventListener('scroll', this.scrollTracker);
    }
  }

  disconnect() {
    super.disconnect();
    if (this.hasScrollableTarget) {
      this.scrollableTarget.removeEventListener('scroll', this.scrollTracker);
    }
  }

  toggle(e) {
    const contentToToggle = this._findById(e.params.id);
    const toggler = e.currentTarget;
    const iconToToggle = this._findById(e.params.iconId);
    if (contentToToggle.hidden) {
      this._expand(toggler, contentToToggle, iconToToggle);
    } else {
      this._collapse(toggler, contentToToggle, iconToToggle);
    }
  }

  search(event) {
    event.preventDefault();
    const searchValue = event.target.querySelector('input')?.value.trim().toLowerCase()
      || event.target.value.trim().toLowerCase();

    const collapseEvent = new Event('collapse:search', {bubbles: true});
    this.element.dispatchEvent(collapseEvent);

    if (searchValue.length === 0) {
      this.searchResultsTarget.hidden = true;
      this.searchableTargets.forEach(target => {
        target.classList.remove(this.searchMatchClass);
      });
      return;
    }


    let matchedCount = 0;
    this.searchableTargets.forEach(target => {
      if (target.innerText.toLowerCase().includes(searchValue)) {
        target.classList.add(this.searchMatchClass);
        this._expandBranch(target.dataset.collapseParentBranchId)
        if (matchedCount === 0) {
          target.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
        matchedCount += 1;
      } else {
        target.classList.remove(this.searchMatchClass);
      }
    })

    this.searchResultsTarget.hidden = false;
    this.searchCountTarget.innerText = matchedCount;
  }

  _expandBranch(branchId) {
    const branch = this._findById(branchId);
    if (!branch) {
      return;
    }

    const toggler = this.element.querySelector(`[data-collapse-id-param="${branchId}"]`)
    const iconToToggle = this._findById(toggler.dataset['collapseIconIdParam']);
    this._expand(toggler, branch, iconToToggle);
  }

  collapseAll() {
    this.element.querySelectorAll('[data-collapse-id-param]').forEach(toggler => {
      const contentToToggle = this._findById(toggler.dataset['collapseIdParam']);
      const iconToToggle = this._findById(toggler.dataset['collapseIconIdParam']);
      this._collapse(toggler, contentToToggle, iconToToggle);
    })
  }

  expandAll() {
    this.element.querySelectorAll('[data-collapse-id-param]').forEach(toggler => {
      const contentToToggle = this._findById(toggler.dataset['collapseIdParam']);
      const iconToToggle = this._findById(toggler.dataset['collapseIconIdParam']);
      this._expand(toggler, contentToToggle, iconToToggle);
    })
  }

  _findById(id) {
    return this.element.querySelector(`#${id}`);
  }

  _expand(toggler, contentToToggle, iconToToggle) {
    contentToToggle.hidden = false;
    contentToToggle.classList.remove(this.hiddenClass);
    iconToToggle.classList.remove(this.expandClass);
    iconToToggle.classList.add(this.collapseClass);
    toggler.classList.remove(this.expandClass);
    toggler.classList.add(this.collapseClass);

    this._storeOpenedNodeInLocalStorage(contentToToggle.id);
  }

  _collapse(toggler, contentToToggle, iconToToggle) {
    contentToToggle.hidden = true;
    contentToToggle.classList.add(this.hiddenClass);
    iconToToggle.classList.remove(this.collapseClass);
    iconToToggle.classList.add(this.expandClass);
    toggler.classList.remove(this.collapseClass);
    toggler.classList.add(this.expandClass);

    this._removeOpenedNodeFromLocalStorage(contentToToggle.id);
  }

  _initFromLocalStorage() {
    this._openedIdsInLocalStorage().forEach(id => {
      const toggler = this.element.querySelector(`[data-collapse-id-param=${id}]`);
      if (!toggler) {
        return;
      }
      this._expand(
        toggler,
        this._findById(toggler.dataset['collapseIdParam']),
        this._findById(toggler.dataset['collapseIconIdParam']),
      );
    });
    const scrollPosition = localStorage.getItem(this.scrollLocalStorageKeyValue);
    if (scrollPosition && this.hasScrollableTarget) {
      this.scrollableTarget.scrollTop = parseInt(scrollPosition, 10);
    }
  }

  _openedIdsInLocalStorage() {
    const rawValue = localStorage.getItem(this.localStorageKeyValue);
    if (!rawValue) {
      return []
    }
    try {
      return JSON.parse(rawValue);
    } catch (SyntaxError) {
      localStorage.removeItem(this.localStorageKeyValue);
      return []
    }
  }

  _storeOpenedNodeInLocalStorage(id) {
    const openedIds = this._openedIdsInLocalStorage();
    if (!openedIds.includes(id)) {
      openedIds.push(id);
    }
    this._storeOpenedIdsInLocalStorage(openedIds);
  }

  _removeOpenedNodeFromLocalStorage(id) {
    this._storeOpenedIdsInLocalStorage(
      this._openedIdsInLocalStorage().filter(v => v !== id)
    );
  }

  _storeOpenedIdsInLocalStorage(openedIds) {
    localStorage.setItem(this.localStorageKeyValue, JSON.stringify(openedIds));
  }

  _trackScroll() {
    if (this.hasScrollableTarget) {
      setTimeout(() => {
        localStorage.setItem(this.scrollLocalStorageKeyValue, this.scrollableTarget.scrollTop);
      }, 1000);
    }
  }
}